/**
 * Assisted Booking KPI Dashboard
 * Admin → Operations → Assisted Booking KPI
 * Live operational control and monitoring
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  ArrowLeft, Activity, Package, CreditCard, TrendingUp, Clock,
  AlertTriangle, CheckCircle, XCircle, Users, Download, Bell,
  Loader2, RefreshCw, BarChart3, Eye, ChevronDown, Filter,
  Zap, ShieldCheck, Truck, MapPin
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AssistedBookingKPI = () => {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState("today");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpiData, setKpiData] = useState(null);
  const [funnelData, setFunnelData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [liveQueue, setLiveQueue] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [activeTab, setActiveTab] = useState("kpis");

  useEffect(() => {
    fetchAllData();
  }, [dateFilter]);

  // Auto-refresh every 30 seconds for live data
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === "live") {
        fetchLiveQueue();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [kpiRes, funnelRes, alertsRes, queueRes, healthRes] = await Promise.all([
        fetch(`${API}/api/admin/assisted-booking-kpi/dashboard?date_filter=${dateFilter}`),
        fetch(`${API}/api/admin/assisted-booking-kpi/funnel?date_filter=${dateFilter}`),
        fetch(`${API}/api/admin/assisted-booking-kpi/alerts`),
        fetch(`${API}/api/admin/assisted-booking-kpi/live-queue?limit=20`),
        fetch(`${API}/api/admin/assisted-booking-kpi/health`)
      ]);

      const [kpi, funnel, alertsData, queue, health] = await Promise.all([
        kpiRes.json(), funnelRes.json(), alertsRes.json(), queueRes.json(), healthRes.json()
      ]);

      if (kpi.success) setKpiData(kpi.kpis);
      if (funnel.success) setFunnelData(funnel.funnel);
      if (alertsData.success) setAlerts(alertsData.alerts);
      if (queue.success) setLiveQueue(queue.queue);
      setHealthData(health);
    } catch (err) {
      toast.error("Failed to load KPI data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveQueue = async () => {
    try {
      const res = await fetch(`${API}/api/admin/assisted-booking-kpi/live-queue?limit=20`);
      const json = await res.json();
      if (json.success) setLiveQueue(json.queue);
    } catch (err) {
      console.error("Failed to refresh queue");
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const KPICard = ({ icon: Icon, label, value, suffix = "", color = "emerald", trend, alert }) => (
    <div className={`bg-slate-800/50 rounded-xl p-4 border ${alert ? 'border-red-500/50' : 'border-slate-700/50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
          <Icon className={`h-5 w-5 text-${color}-400`} />
        </div>
        {alert && <AlertTriangle className="h-4 w-4 text-red-400" />}
      </div>
      <p className="text-slate-400 text-sm">{label}</p>
      <p className="text-2xl font-bold text-white mt-1">
        {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
      {trend !== undefined && (
        <p className={`text-xs mt-1 ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period
        </p>
      )}
    </div>
  );

  const AlertCard = ({ alert }) => {
    const colors = {
      error: "border-red-500/50 bg-red-500/10",
      warning: "border-amber-500/50 bg-amber-500/10",
      info: "border-blue-500/50 bg-blue-500/10"
    };
    const icons = {
      error: <XCircle className="h-5 w-5 text-red-400" />,
      warning: <AlertTriangle className="h-5 w-5 text-amber-400" />,
      info: <Bell className="h-5 w-5 text-blue-400" />
    };

    return (
      <div className={`rounded-xl p-4 border ${colors[alert.type]}`}>
        <div className="flex items-start gap-3">
          {icons[alert.type]}
          <div className="flex-1">
            <p className="text-white font-medium">{alert.title}</p>
            <p className="text-slate-400 text-sm mt-1">{alert.message}</p>
          </div>
          <span className="text-2xl font-bold text-white">{alert.count}</span>
        </div>
      </div>
    );
  };

  const getStatusIndicator = (status) => {
    const styles = {
      ready: { bg: "bg-emerald-500", text: "Ready for Pickup" },
      paid_not_visible: { bg: "bg-red-500", text: "Paid - Not Visible!" },
      awaiting_payment: { bg: "bg-amber-500", text: "Awaiting Payment" }
    };
    const s = styles[status] || styles.awaiting_payment;
    return (
      <span className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${s.bg}`}></span>
        <span className="text-xs text-slate-400">{s.text}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-400">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-emerald-500" />
                  Assisted Booking KPI
                </h1>
                <p className="text-slate-400 text-sm">Operations → KPI Monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Health Status Badge */}
              <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 ${
                healthData?.status === 'healthy' 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                <span className={`w-2 h-2 rounded-full ${healthData?.status === 'healthy' ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`}></span>
                {healthData?.status === 'healthy' ? 'System Healthy' : 'System Degraded'}
              </div>
              
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="today" className="text-white">Today</SelectItem>
                  <SelectItem value="this_week" className="text-white">This Week</SelectItem>
                  <SelectItem value="this_month" className="text-white">This Month</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="border-slate-700">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 mt-4">
            {[
              { id: "kpis", label: "KPIs", icon: BarChart3 },
              { id: "funnel", label: "Funnel", icon: Filter },
              { id: "alerts", label: `Alerts (${alerts.length})`, icon: AlertTriangle },
              { id: "live", label: "Live Queue", icon: Zap },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* KPIs Tab */}
        {activeTab === "kpis" && kpiData && (
          <div className="space-y-6">
            {/* Main KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <KPICard icon={Package} label="Bookings Today" value={kpiData.bookings_today} color="blue" />
              <KPICard icon={CheckCircle} label="Paid Today" value={kpiData.paid_today} color="emerald" />
              <KPICard icon={Clock} label="Pending Today" value={kpiData.pending_today} color="amber" />
              <KPICard icon={TrendingUp} label="Conversion Today" value={kpiData.conversion_today} suffix="%" color="purple" />
              <KPICard icon={TrendingUp} label="Conversion Week" value={kpiData.conversion_week} suffix="%" color="indigo" />
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <KPICard icon={Clock} label="Avg Payment Time" value={kpiData.avg_payment_time_mins} suffix=" min" color="cyan" />
              <KPICard icon={XCircle} label="Failed Payments" value={kpiData.failed_payments} color="red" alert={kpiData.failed_payments > 0} />
              <KPICard icon={ShieldCheck} label="Webhook Success" value={kpiData.webhook_success_rate} suffix="%" color="emerald" />
              <KPICard icon={Download} label="Label Download Rate" value={kpiData.label_download_rate} suffix="%" color="blue" />
              <KPICard icon={Truck} label="Driver Accept Rate" value={kpiData.driver_acceptance_rate} suffix="%" color="amber" />
            </div>

            {/* Operational Health */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard icon={Eye} label="Visible to Drivers" value={kpiData.visible_after_payment} color="emerald" />
              <KPICard icon={AlertTriangle} label="Missing Details" value={kpiData.missing_details_count} color="red" alert={kpiData.missing_details_count > 0} />
              <KPICard icon={Bell} label="Notif Failures" value={kpiData.notification_failures} color="red" alert={kpiData.notification_failures > 0} />
              <KPICard icon={Users} label="Active Agents" value={kpiData.agent_leaderboard?.length || 0} color="purple" />
            </div>

            {/* Agent Leaderboard */}
            {kpiData.agent_leaderboard?.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-amber-500" />
                  Today's Leaderboard
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  {kpiData.agent_leaderboard.map((agent, i) => (
                    <div key={agent.agent_id} className={`p-3 rounded-lg ${i === 0 ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-slate-900'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${i === 0 ? 'text-amber-400' : 'text-slate-500'}`}>#{i + 1}</span>
                        <div>
                          <p className="text-white text-sm font-medium">{agent.agent_name}</p>
                          <p className="text-slate-500 text-xs">{agent.bookings_today} bookings</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Funnel Tab */}
        {activeTab === "funnel" && (
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h3 className="text-white font-semibold mb-6">Booking Funnel</h3>
            <div className="space-y-3">
              {funnelData.map((stage, i) => {
                const maxCount = Math.max(...funnelData.map(s => s.count)) || 1;
                const widthPercent = (stage.count / maxCount) * 100;
                
                return (
                  <div key={stage.stage} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-slate-400 text-sm capitalize">{stage.stage.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{stage.count}</span>
                        {stage.drop_off_percent > 0 && (
                          <span className="text-red-400 text-xs">-{stage.drop_off_percent}%</span>
                        )}
                      </div>
                    </div>
                    <div className="h-8 bg-slate-900 rounded-lg overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          i === 0 ? 'bg-blue-500' :
                          i < 4 ? 'bg-purple-500' :
                          i < 7 ? 'bg-emerald-500' :
                          'bg-amber-500'
                        }`}
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                    {i < funnelData.length - 1 && (
                      <div className="flex justify-center py-1">
                        <ChevronDown className="h-4 w-4 text-slate-600" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === "alerts" && (
          <div className="space-y-4">
            {alerts.length === 0 ? (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
                <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-emerald-400 font-medium">All Systems Operational</p>
                <p className="text-slate-500 text-sm">No alerts at this time</p>
              </div>
            ) : (
              alerts.map((alert, i) => <AlertCard key={i} alert={alert} />)
            )}
          </div>
        )}

        {/* Live Queue Tab */}
        {activeTab === "live" && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Live Booking Queue
              </h3>
              <span className="text-slate-500 text-sm">Auto-refreshes every 30s</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800">
                  <tr className="text-left text-xs text-slate-400 uppercase">
                    <th className="px-4 py-3">Tracking ID</th>
                    <th className="px-4 py-3">Sender</th>
                    <th className="px-4 py-3">Recipient</th>
                    <th className="px-4 py-3">Route</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {liveQueue.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                        No bookings in queue
                      </td>
                    </tr>
                  ) : (
                    liveQueue.map((booking) => (
                      <tr key={booking.id} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-mono text-amber-400">{booking.tracking_id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white text-sm">{booking.sender_name}</p>
                            <p className="text-slate-500 text-xs">{booking.sender_phone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white text-sm">{booking.recipient_name}</p>
                            <p className="text-slate-500 text-xs">{booking.recipient_phone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-xs">
                            <MapPin className="h-3 w-3 text-emerald-400" />
                            <span className="text-slate-400">{booking.pickup_address?.parish}</span>
                            <span className="text-slate-600">→</span>
                            <MapPin className="h-3 w-3 text-purple-400" />
                            <span className="text-slate-400">{booking.dropoff_address?.parish}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-emerald-400 font-medium">
                          J${booking.total_price?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusIndicator(booking.status_indicator)}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {new Date(booking.created_at).toLocaleTimeString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AssistedBookingKPI;
